import { DonorStatus } from 'gql/types';

import { ITableMeta, PendingDaysStatus } from './types';

import { DateRangeType } from '../../types/phlebotomist-dashboard';

export const SETTINGS_TAB = 'recruiter-dashboard';

export const DATA_TABLE_META: Record<string, ITableMeta> = {
  'prbc-donations': {
    title: 'pRBC Donations - Donors',
    description:
      'Donors linked to pRBC donations within the selected period and filters.',
    iconColor: '#0b345f',
  },
  'prbc-donations-quarterly': {
    title: 'pRBC Donations - Donors (Quarterly)',
    description: 'Donors linked to pRBC donations for the current quarter.',
    iconColor: '#4f6d7a',
  },
  'ffp-donations': {
    title: 'FFP Units',
    description:
      'Individual FFP product units within the selected period and filters.',
    iconColor: '#7b2d8e',
  },
  'active-donors': {
    title: 'Active Donors List',
    description: 'Complete list of all currently active donors in the program.',
    iconColor: '#52c41a',
  },
  'pending-donors': {
    title: 'Pending Donors List',
    description:
      'Complete list of all donors with a pending status, awaiting approval or initial screening.',
    iconColor: '#faad14',
  },
  'approved-donors': {
    title: 'Approved Donors List',
    description:
      'Complete list of all donors with an approved status, cleared for donation.',
    iconColor: '#faad14',
  },
  'single-donors': {
    title: 'Single Donors',
    description: 'Donors who have donated a single unit.',
    iconColor: '#faad14',
  },
  'double-donors': {
    title: 'Double Donors',
    description: 'Donors who have qualified and donated double units.',
    iconColor: '#52c41a',
  },
  'registrations-week': {
    title: 'New Registrations - This Week',
    description: 'Donors registered in the last 7 days.',
    iconColor: '#2f3a45',
  },
  'registrations-month': {
    title: 'New Registrations - This Month',
    description: 'Donors registered in the last 30 days.',
    iconColor: '#0b345f',
  },
  'registrations-quarter': {
    title: 'New Registrations - This Quarter',
    description: 'Donors registered in the last 90 days.',
    iconColor: '#0e4d8f',
  },
  'registrations-year': {
    title: 'New Registrations - This Year',
    description: 'Donors registered in the last 365 days.',
    iconColor: '#1565c0',
  },
  'new-donors-yesterday': {
    title: 'New Donors - Yesterday',
    description: 'Donors who completed their first donation yesterday.',
    iconColor: '#f31a25',
  },
  'recruiter-donors': {
    title: 'Recruited Donors',
    description:
      'Donors recruited by this recruiter during the selected period.',
    iconColor: '#0e4d8f',
  },
  'recruiter-locations': {
    title: 'Recruiter Assigned Locations',
    description:
      'Locations assigned to this recruiter, with active donor count and latest donation date.',
    iconColor: '#0e4d8f',
  },
};

export const VALID_DATE_RANGES: ReadonlySet<string> = new Set<DateRangeType>([
  'day',
  'yesterday',
  'week',
  'lastWeek',
  'month',
  'lastMonth',
  'quarter',
  'lastQuarter',
  'year',
  'lastYear',
  'dayCal',
  'yesterdayCal',
  'weekCal',
  'lastWeekCal',
  'monthCal',
  'lastMonthCal',
  'quarterCal',
  'lastQuarterCal',
  'yearCal',
  'pYearCal',
  'custom',
]);

export const UNIT_TABLE_CONFIG_IDS = new Set([
  'prbc-donations',
  'prbc-donations-quarterly',
  'ffp-donations',
]);

export const DONOR_TABLE_CONFIG_IDS = new Set([
  'active-donors',
  'pending-donors',
  'approved-donors',
  'single-donors',
  'double-donors',
  'registrations-week',
  'registrations-month',
  'registrations-quarter',
  'registrations-year',
  'new-donors-yesterday',
]);

export const REGISTRATION_CONFIGS = new Set([
  'registrations-week',
  'registrations-month',
  'registrations-quarter',
  'registrations-year',
  'new-donors-yesterday',
]);

export const DEFAULT_DATE_RANGE_FALLBACK: DateRangeType = 'monthCal';

export const DATE_FORMAT = 'MM/dd/yyyy';

export const SGL_DBL_OPTIONS = [
  { text: 'SGL', value: 'SGL' },
  { text: 'DBL', value: 'DBL' },
];

export const BLOOD_TYPE_OPTIONS = [
  { text: 'P (Positive)', value: 'P' },
  { text: 'N (Negative)', value: 'N' },
  { text: 'U (Unknown)', value: 'U' },
];

export const PENDING_DAYS_FILTER_OPTIONS = [
  { text: 'On Track', value: 'onTrack' },
  { text: 'Approaching', value: 'approaching' },
  { text: 'Overdue', value: 'overdue' },
];

export const CONTACTED_FILTER_OPTIONS = [
  { text: 'Contacted', value: 'yes' },
  { text: 'Not Contacted', value: 'no' },
];

export const UNIT_CATEGORY_IDS = {
  FFP: 33,
  PRBC: 31,
} as const;

export const STATUS_COLORS: Record<string, string> = {
  [DonorStatus.Active]: '#52c41a',
  [DonorStatus.Approved]: '#1565c0',
  [DonorStatus.Pending]: '#faad14',
  [DonorStatus.Registered]: '#722ed1',
  [DonorStatus.Retired]: '#8c8c8c',
  [DonorStatus.Archived]: '#8c8c8c',
  [DonorStatus.AnnualLabworkDue]: '#1890ff',
};

export const LINK_COLOR = '#1565c0';

export const TYPE_COLORS = {
  DBL: '#f31a25',
  SGL: LINK_COLOR,
};

export const PENDING_DAYS_ROW_COLORS: Record<PendingDaysStatus, string> = {
  onTrack: '#f6ffed',
  approaching: '#fffbe6',
  overdue: '#fff2f0',
};
